import { AnalysisService, type Analysis } from './analysis.service';
import { toastService } from './toast.service';
import type { QueryClient } from '@tanstack/react-query';

const analysisService = new AnalysisService();

export interface SaveChartParams {
  analysis: Analysis;
  analysisId?: string;
  name: string;
  sortOrder?: any[];
  queryClient: QueryClient;
}

export async function saveChart({ analysis, analysisId, name, sortOrder, queryClient }: SaveChartParams): Promise<Analysis> {
  const payload: Partial<Analysis> = {
    name,
    visualization_type: analysis.visualization_type,
    dimension_ids: analysis.dimension_ids || [],
    measure_ids: analysis.measure_ids || [],
    dimension_filters: analysis.dimension_filters || [],
    date_range: analysis.date_range,
    custom_date_range_start: analysis.custom_date_range_start,
    custom_date_range_end: analysis.custom_date_range_end,
    default_sort_order: sortOrder || analysis.default_sort_order,
    custom_parameters: analysis.custom_parameters,
    limit: analysis.limit,
    viz_options: analysis.viz_options,
    metric_filters: analysis.metric_filters,
    date_comparison: analysis.date_comparison,
    ignore_empty_dimensions: analysis.ignore_empty_dimensions,
    dimension_include_all: analysis.dimension_include_all,
    ephemeral: false,
  };

  try {
    let result: Analysis;

    const isNew = !analysisId || analysis.ephemeral;

    if (analysisId) {
      result = await analysisService.update(analysisId, payload);
    } else {
      result = await analysisService.create(payload as Omit<Analysis, 'id' | 'last_updated'>);
    }

    if (isNew) {
      toastService.success('Chart saved successfully', {
        description: 'It is now available on the Analytics page.',
      });
    } else {
      toastService.success('Chart updated successfully');
    }

    queryClient.invalidateQueries({ queryKey: ['analyses'] });
    return result;
  } catch (error) {
    console.error('Failed to save chart:', error);
    toastService.error('Failed to save chart', {
      description: error instanceof Error ? error.message : 'An unexpected error occurred',
    });
    throw error;
  }
}
